import React, { useState, useEffect, useRef } from 'react';
import { RadarChartComponent, RadarDataset } from './RadarChart';
import { RadarPreferenceEditor } from './RadarPreferenceEditor';
import { HEAD_AXES, SHAFT_AXES, IRON_HEAD_AXES, IRON_SHAFT_AXES } from '../../config/radarAxes';
import { saveRadarPreferences, loadRadarPreferences } from '../../services/recommendationService';

interface RadarProfilePanelProps {
  headRadar: Record<string, number>;
  shaftRadar: Record<string, number>;
  profileId?: number;
  equipmentType?: string;
  onPreferencesChange?: (prefs: { head: Record<string, number>; shaft: Record<string, number> }) => void;
}

const STORAGE_KEY = 'equipment_preferences';

export const RadarProfilePanel: React.FC<RadarProfilePanelProps> = ({
  headRadar,
  shaftRadar,
  profileId,
  equipmentType = 'driver',
  onPreferencesChange,
}) => {
  const [activeTab, setActiveTab] = useState<'head' | 'shaft'>('head');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isIron = equipmentType === 'iron';
  const headAxes = isIron ? IRON_HEAD_AXES : HEAD_AXES;
  const shaftAxes = isIron ? IRON_SHAFT_AXES : SHAFT_AXES;
  const storageKey = isIron ? `${STORAGE_KEY}_iron` : STORAGE_KEY;

  const [headPrefs, setHeadPrefs] = useState<Record<string, number>>(
    () => Object.fromEntries(headAxes.map(a => [a.key, a.defaultPreference]))
  );
  const [shaftPrefs, setShaftPrefs] = useState<Record<string, number>>(
    () => Object.fromEntries(shaftAxes.map(a => [a.key, a.defaultPreference]))
  );

  // Load saved preferences on mount — DB first, localStorage fallback
  useEffect(() => {
    // Reset defaults when equipment type changes
    setHeadPrefs(Object.fromEntries(headAxes.map(a => [a.key, a.defaultPreference])));
    setShaftPrefs(Object.fromEntries(shaftAxes.map(a => [a.key, a.defaultPreference])));

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.head) setHeadPrefs(parsed.head);
        if (parsed.shaft) setShaftPrefs(parsed.shaft);
      }
    } catch {
      // ignore malformed storage
    }

    if (profileId) {
      loadRadarPreferences(profileId, equipmentType)
        .then(saved => {
          if (Object.keys(saved.head).length) setHeadPrefs(saved.head);
          if (Object.keys(saved.shaft).length) setShaftPrefs(saved.shaft);
        })
        .catch(() => { /* localStorage values already loaded above */ });
    }
  }, [profileId, equipmentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTimer = useRef<NodeJS.Timeout>();

  // Persist to localStorage + debounced DB save whenever preferences change
  useEffect(() => {
    const prefs = { head: headPrefs, shaft: shaftPrefs };
    localStorage.setItem(storageKey, JSON.stringify(prefs));
    onPreferencesChange?.(prefs);

    if (profileId) {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveRadarPreferences(profileId, prefs, equipmentType).catch(() => { /* silent — localStorage is fallback */ });
      }, 1000);
    }
  }, [headPrefs, shaftPrefs]); // eslint-disable-line react-hooks/exhaustive-deps

  const axes = activeTab === 'head' ? headAxes : shaftAxes;
  const recommended = activeTab === 'head' ? headRadar : shaftRadar;
  const prefs = activeTab === 'head' ? headPrefs : shaftPrefs;
  const setPrefs = activeTab === 'head' ? setHeadPrefs : setShaftPrefs;

  const datasets: RadarDataset[] = [
    { name: 'Recommended', color: '#3B82F6', fillOpacity: 0.2, values: recommended },
    { name: 'Your Preference', color: '#F97316', fillOpacity: 0.15, values: prefs },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold mb-1">Equipment Profile</h3>
      <p className="text-sm text-gray-500 mb-4">
        <span className="text-blue-500 font-medium">Blue</span> = recommended based on your data.{' '}
        <span className="text-orange-500 font-medium">Orange</span> = your preferences. Adjust sliders to refine.
      </p>

      {/* Head / Shaft tabs */}
      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setActiveTab('head')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeTab === 'head'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {isIron ? 'Iron Head' : 'Driver Head'}
        </button>
        <button
          onClick={() => setActiveTab('shaft')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeTab === 'shaft'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Shaft
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
        <RadarChartComponent axes={axes} datasets={datasets} size={isMobile ? 'sm' : 'md'} />
        <RadarPreferenceEditor
          axes={axes}
          values={prefs}
          onChange={(key, val) => setPrefs(prev => ({ ...prev, [key]: val }))}
          title="Adjust Your Preferences"
        />
      </div>
    </div>
  );
};
